import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import type { ProfileSummary, ProjectSummary } from "../types";
import {
	FEEDBACK_KINDS,
	reportOf,
	researchArtifactHref,
	researchApiBase,
	researchArtifactTargets,
	researchRecordTitle,
	researchScopeKey,
	VERIFICATION_REPORT_KIND,
	type AnalysisSummary,
	type AnalysisView,
	type AnchorLocator,
	type AssessmentBody,
	type DraftBody,
	type EvidenceBody,
	type EvidenceImpacts,
	type EvidenceSummary,
	type FeedbackBody,
	type ImpactItem,
	type QualitySummary,
	type RecordRead,
	type ResearchCapabilities,
	type ResearchMapView,
	type ResearchRecord,
	type ResearchScopeRef,
	type ResearchTarget,
	type SourceBody,
	type StoredRecord,
} from "../../../shared/wire/research.ts";
import { ChevronLeftIcon, ChevronRightIcon, MenuIcon } from "../icons";
import { useIsNarrow } from "../viewport";
import { usePhoneDetail } from "./phonePage";
import { analysisRecord, RESEARCH_CHANGED, ReportSection, ResearchViewBody } from "../components/Research";
import { ResearchMapMeta, ResearchMapPanel, selectionFor, type ResearchSelection } from "../components/ResearchMap";
import { AnalysisSection } from "../components/ResearchAnalysis";
import { DecisionDetail } from "../components/ResearchDecision";
import { FeedbackFields, QualitySummaryCard, RecurrenceTable } from "../components/ResearchFeedback";
import {
	anchorStatusLabel,
	authorityLabel,
	existenceLabel,
	formatLabel,
	impactStatusLabel,
	kindLabel,
	readingLabel,
	relationLabel,
	sourceStatusLabel,
	summaryLabel,
	supportLabel,
	timeLabel,
} from "../components/researchLabels";
import {
	acknowledgeRequest,
	analysisCounts,
	analysisPageView,
	BUSY_LABELS,
	decisionFailure,
	decisionFor,
	historyRows,
	isPending,
	LOAD_LABELS,
	newestOf,
	pageNotices,
	recordGroups,
	recordReads,
	researchScopes,
	revisionState,
} from "./researchPage";

/** Where a link opened the page, at a scope or at the one already shown; `at` tells a second click on the same link from the first. */
export interface ResearchLink {
	scope?: ResearchScopeRef;
	record?: string;
	/** `map` opens the project's research map. */
	view?: "map";
	at: number;
}

const INBOX: ResearchScopeRef = { kind: "unassigned", collectionId: "inbox" };

type Selection = { kind: "record"; id: string } | { kind: "evidence" } | { kind: "impacts" } | { kind: "map" } | { kind: "analysis" } | { kind: "quality" } | null;

const MAP_DEPTH = 4;

const selectionOf = (link: ResearchLink | null): Selection => (link?.record ? { kind: "record", id: link.record } : link?.view === "map" && link.scope?.kind === "project" ? { kind: "map" } : null);

interface ScopeData {
	capabilities: ResearchCapabilities;
	/** Each record at its effective revision, with its newest beside it when that is another one. */
	reads: RecordRead[];
	evidence: EvidenceSummary | { error: string };
	impacts: EvidenceImpacts | { error: string };
	/** A project's research map; none for the inbox. */
	map: ResearchMapView | { error: string } | null;
	analysis: AnalysisSummary | { error: string };
	quality: QualitySummary | { error: string };
}

const ANALYSIS_KINDS = new Set(["run", "plan", "input", "environment"]);

const failure = (err: unknown) => (err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));

/**
 * The owner's research records, per scope: one of their research projects or their unassigned inbox. Every
 * revision reads here with its state, authority and origin, a finalized report with its disclosure; confirming
 * or rejecting one, and acknowledging a reviewed paragraph, happen only here and only for exactly what is on screen.
 */
export function ResearchPage({ me, projects, profiles, link, onMenu }: { me: string; projects: ProjectSummary[]; profiles: ProfileSummary[]; link: ResearchLink | null; onMenu: () => void }) {
	const [scope, setScope] = useState<ResearchScopeRef>(link?.scope ?? INBOX);
	const [selection, setSelection] = useState<Selection>(selectionOf(link));
	const [data, setData] = useState<ScopeData | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const phone = usePhoneDetail(useIsNarrow(), "researchPage", () => setSelection(null));

	useEffect(() => {
		if (!link) return;
		if (link.scope) setScope(link.scope);
		const next = selectionOf(link);
		setSelection(next);
		if (next) phone.opened();
	}, [link]);

	const options = useMemo(() => researchScopes(me, projects, profiles, scope), [me, projects, profiles, scope]);
	const key = researchScopeKey(scope);

	const load = useCallback(async () => {
		try {
			const [capabilities, list, evidence, impacts, map, analysis, quality] = await Promise.all([
				api.researchCapabilities(scope),
				api.researchRecords(scope),
				api.researchEvidence(scope).catch((err: unknown) => ({ error: failure(err) })),
				api.researchImpacts(scope).catch((err: unknown) => ({ error: failure(err) })),
				scope.kind === "project" ? api.researchMap(scope).catch((err: unknown) => ({ error: failure(err) })) : null,
				api.researchAnalysis(scope).catch((err: unknown) => ({ error: failure(err) })),
				api.researchQuality(scope).catch((err: unknown) => ({ error: failure(err) })),
			]);
			setData({ capabilities, reads: recordReads(list), evidence, impacts, map, analysis, quality });
			setLoadError(null);
		} catch (err) {
			setLoadError(failure(err));
		}
	}, [key]);

	useEffect(() => {
		setData(null);
		setLoadError(null);
		void load();
		window.addEventListener(RESEARCH_CHANGED, load);
		return () => window.removeEventListener(RESEARCH_CHANGED, load);
	}, [load]);

	/** After an owner action: this page reloads, and every research view in the chat refetches. */
	const changed = useCallback(() => {
		window.dispatchEvent(new Event(RESEARCH_CHANGED));
	}, []);

	const pick = (next: Selection) => {
		setSelection(next);
		setError(null);
		phone.opened();
	};

	const grouped = useMemo(() => recordGroups(data?.reads ?? []), [data]);
	const known = useMemo(() => (data?.reads ?? []).map((r) => r.record), [data]);

	const evidence = data && "claims" in data.evidence ? data.evidence : null;
	const impacts = data && "paragraphs" in data.impacts ? data.impacts : null;
	const quality = data && "status" in data.quality ? data.quality : null;
	const analysis = data && "runs" in data.analysis ? data.analysis : null;
	const notices = data ? pageNotices(data.capabilities, [...(evidence?.notices ?? []), ...(impacts?.notices ?? []), ...(analysis?.notices ?? [])]) : [];
	const scopeName = options.find((o) => o.key === key)?.label ?? "";

	return (
		<main className={`chat research-page split-page${selection ? " detail-open" : ""}`}>
			{error && (
				<p className="banner error">
					{error}
					<button className="banner-close" title="关闭" onClick={() => setError(null)}>
						×
					</button>
				</p>
			)}
			<div className="split">
				<section className="split-list">
					<header className="chat-head page-head">
						<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
							<MenuIcon size={22} />
						</button>
						<div className="titles">
							<span className="title">研究</span>
							{data && (
								<span className="sub">
									{data.reads.length} 条记录 · {data.reads.filter(isPending).length} 条待确认
								</span>
							)}
						</div>
					</header>
					<div className="split-scroll">
						<div className="list-tools">
							<div className="chip-row" role="group" aria-label="范围">
								{options.map((o) => (
									<button
										key={o.key}
										className={`chip${o.key === key ? " on" : ""}`}
										aria-pressed={o.key === key}
										onClick={() => {
											if (o.key === key) return;
											setScope(o.scope);
											setSelection(null);
										}}
									>
										{o.label}
									</button>
								))}
							</div>
							{notices.map((n, i) => (
								<p key={i} className={`research-notice ${n.kind}`}>
									{n.heading}
									{n.message ? `：${n.message}` : ""}
								</p>
							))}
						</div>
						{loadError ? (
							<p className="hint list-hint">
								{LOAD_LABELS.failed}：{loadError}
							</p>
						) : !data ? (
							<p className="hint list-hint">{LOAD_LABELS.loading}</p>
						) : (
							<>
								<div className="row-group">
									{data.map && (
										<button className={`list-row${selection?.kind === "map" ? " current" : ""}`} onClick={() => pick({ kind: "map" })}>
											<span className="list-row-title">研究地图</span>
											<span className="list-row-meta">{"error" in data.map ? LOAD_LABELS.failed : `${data.map.nodes.length} 个节点 · ${data.map.gaps.length} 个缺口`}</span>
										</button>
									)}
									<button className={`list-row${selection?.kind === "evidence" ? " current" : ""}`} onClick={() => pick({ kind: "evidence" })}>
										<span className="list-row-title">证据</span>
										<span className="list-row-meta">{evidence ? `${evidence.claims.length} 个主张 · ${evidence.needsReview.length} 处待复查` : `${LOAD_LABELS.failed}`}</span>
									</button>
									<button className={`list-row${selection?.kind === "impacts" ? " current" : ""}`} onClick={() => pick({ kind: "impacts" })}>
										<span className="list-row-title">受影响段落</span>
										<span className="list-row-meta">{impacts ? `${impacts.paragraphs.filter((p) => !p.acknowledged).length} 处待复查` : LOAD_LABELS.failed}</span>
									</button>
									{data.capabilities.domains.analysis.available && (
										<button className={`list-row${selection?.kind === "analysis" ? " current" : ""}`} onClick={() => pick({ kind: "analysis" })}>
											<span className="list-row-title">分析</span>
											<span className="list-row-meta">{analysis ? analysisCounts(analysis) : LOAD_LABELS.failed}</span>
										</button>
									)}
									<button className={`list-row${selection?.kind === "quality" ? " current" : ""}`} onClick={() => pick({ kind: "quality" })}>
										<span className="list-row-title">质量</span>
										<span className="list-row-meta">{quality ? `${summaryLabel(quality.status)} · ${quality.cases.total} 个用例 · ${quality.comparisons.total} 次对比` : LOAD_LABELS.failed}</span>
									</button>
								</div>
								{grouped.length === 0 && <p className="hint list-hint">暂无记录</p>}
								{grouped.map((g) => (
									<Fragment key={g.kind}>
										<h3 className="list-label">{kindLabel(g.kind)}</h3>
										<div className="row-group">
											{g.reads.map(({ record: r, latest: pending }) => {
												return (
													<button key={r.id} className={`list-row${selection?.kind === "record" && selection.id === r.id ? " current" : ""}`} onClick={() => pick({ kind: "record", id: r.id })}>
														<span className="list-row-head">
															<span className="list-row-title">{researchRecordTitle(r)}</span>
															<span className={`pill research-status ${r.unsupported ? "unsupported" : r.status}`}>{revisionState(r)}</span>
														</span>
														<span className="list-row-meta">
															{[`r${r.revision}`, r.unsupported ? "" : authorityLabel(r.authority), timeLabel(r.createdAt), pending ? `r${pending.revision} ${revisionState(pending)}` : ""].filter(Boolean).join(" · ")}
														</span>
													</button>
												);
											})}
										</div>
									</Fragment>
								))}
							</>
						)}
					</div>
				</section>
				<section className="split-detail">
					{selection && data && (
						<>
							<header className="detail-head">
								<button className="ghost icon-btn detail-back" aria-label="返回" title="返回" onClick={phone.back}>
									<ChevronLeftIcon size={24} />
								</button>
								<span className="detail-crumb">
									<span className="detail-crumb-dim">{scopeName}</span>
									<ChevronRightIcon size={14} />
									<span>{selection.kind === "record" ? kindLabel(known.find((r) => r.id === selection.id)?.kind ?? "") || "记录" : selection.kind === "map" ? "研究地图" : selection.kind === "evidence" ? "证据" : selection.kind === "analysis" ? "分析" : selection.kind === "quality" ? "质量" : "受影响段落"}</span>
								</span>
							</header>
							<div className="detail-body">
								{selection.kind === "record" ? (
									<RecordDetail key={`${key}:${selection.id}`} scope={scope} id={selection.id} known={known} onPick={(id) => pick({ kind: "record", id })} onChanged={changed} onError={setError} />
								) : selection.kind === "map" ? (
									<MapDetail key={key} scope={scope} known={known} onChanged={changed} onError={setError} />
								) : selection.kind === "evidence" ? (
									"error" in data.evidence ? (
										<p className="research-notice error">证据{LOAD_LABELS.failed}：{data.evidence.error}</p>
									) : (
										<article className="note-view research-view">
											<ResearchViewBody
												live
												view={{
													section: "evidence_summary",
													operation: "evidence_summary",
													scopeRef: scope,
													snapshotAt: data.evidence.snapshotAt,
													revision: data.evidence.revision,
													records: [],
													notices: [],
													capabilities: data.capabilities,
													targets: researchArtifactTargets(scope, [data.evidence]),
													evidence: data.evidence,
												}}
											/>
										</article>
									)
								) : selection.kind === "analysis" ? (
									"error" in data.analysis ? (
										<p className="research-notice error">分析{LOAD_LABELS.failed}：{data.analysis.error}</p>
									) : (
										<article className="note-view research-view">
											<ResearchViewBody live view={analysisPageView(scope, data.capabilities, data.analysis)} />
										</article>
									)
								) : selection.kind === "quality" ? (
									"error" in data.quality ? (
										<p className="research-notice error">质量{LOAD_LABELS.failed}：{data.quality.error}</p>
									) : (
										<QualityDetail summary={data.quality} reads={data.reads} onPick={(id) => pick({ kind: "record", id })} />
									)
								) : "error" in data.impacts ? (
									<p className="research-notice error">受影响段落{LOAD_LABELS.failed}：{data.impacts.error}</p>
								) : (
									<ImpactList scope={scope} impacts={data.impacts} drafts={evidence?.drafts ?? []} onChanged={changed} onError={setError} />
								)}
							</div>
						</>
					)}
				</section>
			</div>
		</main>
	);
}

type MapFocus = { id: string; depth: number } | null;

/** The project's research map, derived on every read: reloaded on opening, on returning to the page and on 刷新. */
function MapDetail({ scope, known, onChanged, onError }: { scope: ResearchScopeRef; known: StoredRecord[]; onChanged: () => void; onError: (message: string | null) => void }) {
	const [map, setMap] = useState<ResearchMapView | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [focus, setFocus] = useState<MapFocus>(null);
	const [selected, setSelected] = useState<ResearchSelection>(null);
	const [refreshedAt, setRefreshedAt] = useState<number | null>(null);

	const load = useCallback(
		async (manual = false) => {
			try {
				const next = await api.researchMap(scope, focus ? { focus: focus.id, depth: focus.depth } : {});
				setMap(next);
				setLoadError(null);
				if (manual) setRefreshedAt(next.snapshotAt);
			} catch (err) {
				setLoadError(failure(err));
			}
		},
		[scope, focus],
	);

	useEffect(() => {
		void load();
		const onShow = () => document.visibilityState === "visible" && void load();
		const onChange = () => void load();
		window.addEventListener(RESEARCH_CHANGED, onChange);
		document.addEventListener("visibilitychange", onShow);
		return () => {
			window.removeEventListener(RESEARCH_CHANGED, onChange);
			document.removeEventListener("visibilitychange", onShow);
		};
	}, [load]);

	if (loadError && !map) return <p className="research-notice error">研究地图{LOAD_LABELS.failed}：{loadError}</p>;
	if (!map) return <p className="hint list-hint">{LOAD_LABELS.loading}</p>;
	return (
		<article className="note-view research-view">
			{loadError && <p className="research-notice error">刷新{LOAD_LABELS.failed}：{loadError}</p>}
			<div className="research-toolbar">
				<button className="ghost" onClick={() => void load(true)}>
					刷新
				</button>
				{focus && (
					<>
						<span className="hint">
							聚焦 {focus.id} · 深度 {focus.depth}
						</span>
						{focus.depth < MAP_DEPTH && (
							<button className="ghost" onClick={() => setFocus({ ...focus, depth: focus.depth + 1 })}>
								加深
							</button>
						)}
						<button className="ghost" onClick={() => setFocus(null)}>
							显示全部
						</button>
					</>
				)}
			</div>
			<ResearchMapPanel
				map={map}
				scopeRef={scope}
				selected={selected}
				onSelect={setSelected}
				onFocus={(id) => setFocus({ id, depth: 2 })}
				renderRecord={(id) => <RecordDetail key={id} scope={scope} id={id} known={known} onPick={(next) => setSelected(selectionFor(map, next))} onChanged={onChanged} onError={onError} />}
			/>
			<ResearchMapMeta view={map} refreshedAt={refreshedAt} />
		</article>
	);
}

function Links({ targets }: { targets: ResearchTarget[] }) {
	if (targets.length === 0) return null;
	return (
		<p className="research-links">
			{targets.map((t) => (
				<a key={t.href} href={t.href} target="_blank" rel="noreferrer">
					快照 {t.label}
				</a>
			))}
		</p>
	);
}

function locatorLabel(locator: AnchorLocator | { type: "lines"; start: number; end: number } | undefined): string {
	if (!locator) return "";
	if (locator.type === "lines") return locator.start === locator.end ? `第 ${locator.start} 行` : `第 ${locator.start}–${locator.end} 行`;
	if (locator.type === "extracted") return `第 ${locator.paragraph} 段${locator.page ? `（第 ${locator.page} 页）` : ""}`;
	return "";
}

/** One record, loaded with its history; a decision goes out for the revision on screen, and every outcome reloads it. */
function RecordDetail({
	scope,
	id,
	known,
	onPick,
	onChanged,
	onError,
}: {
	scope: ResearchScopeRef;
	id: string;
	known: StoredRecord[];
	onPick: (id: string) => void;
	onChanged: () => void;
	onError: (message: string | null) => void;
}) {
	const [detail, setDetail] = useState<{ read: RecordRead; revisions: StoredRecord[] } | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [shown, setShown] = useState<number | null>(null);
	const [stale, setStale] = useState<string | null>(null);
	const [busy, setBusy] = useState<"confirm" | "reject" | null>(null);

	const load = useCallback(async () => {
		try {
			const [read, history] = await Promise.all([api.researchRecord(scope, id), api.researchHistory(scope, id)]);
			setDetail({ read, revisions: history.revisions });
			setShown(newestOf(read).revision);
			setLoadError(null);
		} catch (err) {
			setLoadError(failure(err));
		}
	}, [scope, id]);

	useEffect(() => {
		void load();
		window.addEventListener(RESEARCH_CHANGED, load);
		return () => window.removeEventListener(RESEARCH_CHANGED, load);
	}, [load]);

	async function decide(action: "confirm" | "reject", request: { id: string; body: { revision: number; reason?: string } }) {
		setBusy(action);
		setStale(null);
		onError(null);
		try {
			await api.researchDecide(scope, action, request);
			// Reloads this record, the list and every research view in the chat.
			onChanged();
		} catch (err) {
			const shownFailure = decisionFailure(action, request.body.revision, err);
			if (shownFailure.stale) {
				setStale(shownFailure.message);
				// A newer revision exists: the list and this record reload to it.
				onChanged();
			} else {
				onError(shownFailure.message);
				await load();
			}
		} finally {
			setBusy(null);
		}
	}

	if (loadError) return <p className="research-notice error">记录{LOAD_LABELS.failed}：{loadError}</p>;
	if (!detail || shown === null) return <p className="hint">{LOAD_LABELS.loading}</p>;
	return (
		<ResearchRecordView
			scope={scope}
			read={detail.read}
			revisions={detail.revisions}
			shown={shown}
			stale={stale}
			busy={busy}
			known={known}
			onShow={setShown}
			onPick={onPick}
			onConfirm={(request) => void decide("confirm", request)}
			onReject={(request) => void decide("reject", request)}
		/>
	);
}

type DecisionRequest = { id: string; body: { revision: number; reason?: string } };

/**
 * A record at the revision on screen: its state, provenance and body (a finalized report with its disclosure and
 * artifacts), the decision on that exact revision when it can be decided, and every revision of the record.
 */
export function ResearchRecordView({
	scope,
	read,
	revisions,
	shown,
	stale,
	busy,
	known = [],
	onShow,
	onPick = () => {},
	onConfirm,
	onReject,
}: {
	scope: ResearchScopeRef;
	read: RecordRead;
	revisions: StoredRecord[];
	shown: number;
	/** Why the last decision was refused as stale; the record has reloaded since. */
	stale: string | null;
	busy: "confirm" | "reject" | null;
	known?: StoredRecord[];
	onShow: (revision: number) => void;
	onPick?: (id: string) => void;
	onConfirm: (request: DecisionRequest) => void;
	onReject: (request: DecisionRequest) => void;
}) {
	const [reason, setReason] = useState<string | null>(null);
	const newest = newestOf(read);
	const record = revisions.find((r) => r.revision === shown) ?? newest;
	const decision = decisionFor(record, newest);
	const effective = record.revision === read.record.revision;

	return (
		<article className="note-view research-view">
			{stale && <p className="research-notice stale_view">{stale}</p>}
			<h1>{researchRecordTitle(record)}</h1>
			<p className="note-meta">
				<span className={`pill research-status ${record.unsupported ? "unsupported" : record.status}`}>{revisionState(record)}</span>
				{[
					`r${record.revision}`,
					record.unsupported ? `schemaVersion ${record.schemaVersion}` : authorityLabel(record.authority),
					record.unsupported ? "" : `${record.origin.actor} · ${record.origin.kind === "agent" ? "代理" : record.origin.kind === "user" ? "用户" : "导入"}${record.origin.sessionId ? ` · 对话 ${record.origin.sessionId}` : ""}`,
					timeLabel(record.createdAt),
					effective ? "生效中" : "",
				]
					.filter(Boolean)
					.join(" · ")}
			</p>
			{record.unsupported && <p className="research-notice unsupported_schema">记录版本未支持：{record.reason}</p>}
			{record.revision !== newest.revision && (
				<p className="research-notice">
					最新版本 r{newest.revision} {revisionState(newest)}{" "}
					<button className="link-btn" onClick={() => onShow(newest.revision)}>
						查看
					</button>
				</p>
			)}
			{!record.unsupported && record.confirmationActor && (
				<p className="research-meta">
					确认：{record.confirmationActor} · r{record.confirmedRevision ?? record.revision}
					{record.confirmedAt ? ` · ${timeLabel(record.confirmedAt)}` : ""}
				</p>
			)}
			{!record.unsupported && record.rejectionReason !== undefined && (
				<p className="research-meta">
					拒绝理由：{record.rejectionReason}
					{record.rejectedBy ? ` · ${record.rejectedBy}` : ""}
					{record.rejectedAt ? ` · ${timeLabel(record.rejectedAt)}` : ""}
				</p>
			)}

			{!record.unsupported && (ANALYSIS_KINDS.has(record.kind) ? <AnalysisRecord scope={scope} record={record} /> : <RecordBody record={record} scope={scope} />)}

			{!record.unsupported && record.refs.length > 0 && (
				<div className="research-block">
					<p className="research-subtitle">引用</p>
					<ul className="research-list">
						{record.refs.map((ref, i) => {
							const target = ref.id ? known.find((r) => r.id === ref.id) : undefined;
							return (
								<li key={i}>
									{kindLabel(ref.kind)}{" "}
									{target ? (
										<button className="link-btn" onClick={() => onPick(target.id)}>
											{researchRecordTitle(target)}
										</button>
									) : (
										(ref.id ?? (typeof ref.path === "string" ? ref.path : typeof ref.url === "string" ? ref.url : ""))
									)}
									{ref.revision !== undefined && <span className="hint"> r{ref.revision}</span>}
								</li>
							);
						})}
					</ul>
				</div>
			)}

			{!record.unsupported && record.kind !== VERIFICATION_REPORT_KIND && <Links targets={researchArtifactTargets(scope, [record.body])} />}

			{decision && (
				<div className="research-decision">
					{reason === null ? (
						<div className="row">
							<button className="primary" disabled={busy !== null} onClick={() => onConfirm(decision.confirm)}>
								{busy === "confirm" ? BUSY_LABELS.confirm : "确认此版本"}
							</button>
							<button className="ghost" disabled={busy !== null} onClick={() => setReason("")}>
								拒绝此版本
							</button>
						</div>
					) : (
						<div className="form">
							<label>
								拒绝理由
								<textarea value={reason} rows={3} onChange={(e) => setReason(e.target.value)} />
							</label>
							<div className="row">
								<button className="primary" disabled={busy !== null || !reason.trim()} onClick={() => onReject(decision.reject(reason.trim()))}>
									{busy === "reject" ? BUSY_LABELS.reject : "拒绝此版本"}
								</button>
								<button className="ghost" disabled={busy !== null} onClick={() => setReason(null)}>
									取消
								</button>
							</div>
						</div>
					)}
				</div>
			)}

			<div className="research-block research-history">
				<p className="research-subtitle">历史</p>
				<ul className="research-list">
					{historyRows(revisions, read).map((row) => (
						<li key={row.record.revision}>
							<button className={`link-btn${row.record.revision === record.revision ? " current" : ""}`} onClick={() => onShow(row.record.revision)}>
								r{row.record.revision}
							</button>{" "}
							{row.state}
							{row.effective && " · 生效中"}
							<span className="hint">
								{" "}
								{row.record.unsupported ? row.record.reason : `${authorityLabel(row.record.authority)} · ${row.record.origin.actor} · ${timeLabel(row.record.createdAt)}`}
								{!row.record.unsupported && row.record.rejectionReason ? ` · 拒绝理由：${row.record.rejectionReason}` : ""}
								{!row.record.unsupported && row.record.kind === "feedback" ? ` · 更正：${(row.record.body as unknown as FeedbackBody).correction}` : ""}
							</span>
						</li>
					))}
				</ul>
			</div>

			{!record.unsupported && (
				<details className="research-raw">
					<summary>原始数据</summary>
					<pre>{JSON.stringify({ refs: record.refs, body: record.body, contentHash: record.contentHash }, null, 2)}</pre>
				</details>
			)}
		</article>
	);
}

/** An analysis record as the analysis domain reads it now: a run, the plan revision on screen, an input with its original checked, an environment. */
function AnalysisRecord({ scope, record }: { scope: ResearchScopeRef; record: StoredRecord }) {
	const [analysis, setAnalysis] = useState<AnalysisView | { error: string } | null>(null);
	useEffect(() => {
		let current = true;
		setAnalysis(null);
		analysisRecord(researchApiBase(scope), record.kind, record.id, record.revision).then(
			(a) => current && setAnalysis(a),
			(err: unknown) => current && setAnalysis({ error: failure(err) }),
		);
		return () => {
			current = false;
		};
	}, [scope, record.kind, record.id, record.revision]);
	if (!analysis) return <p className="hint">{LOAD_LABELS.loading}</p>;
	if ("error" in analysis) return <p className="research-notice error">分析{LOAD_LABELS.failed}：{analysis.error}</p>;
	return <AnalysisSection analysis={analysis} />;
}

/** The fields of a revision's body that a person reads it by, per kind; the quoted original is shown exactly as kept. */
function RecordBody({ record, scope }: { record: Exclude<StoredRecord, { unsupported: true }>; scope: ResearchScopeRef }) {
	const body = record.body;
	if ((FEEDBACK_KINDS as readonly string[]).includes(record.kind)) return <FeedbackFields record={record} scope={scope} />;
	if (record.kind === "decision" || record.kind === "hypothesis") return <DecisionDetail record={record} />;
	switch (record.kind) {
		case VERIFICATION_REPORT_KIND: {
			const report = reportOf(record);
			return (
				<>
					<p className="research-links">
						<a href={researchArtifactHref(record.scopeRef ?? scope, report.artifactId)} target="_blank" rel="noreferrer noopener">
							定稿报告
						</a>
						<a href={researchArtifactHref(record.scopeRef ?? scope, report.input.snapshotArtifactId)} target="_blank" rel="noreferrer noopener">
							原始报告快照
						</a>
					</p>
					<ReportSection report={report} />
				</>
			);
		}
		case "claim":
			return <p className="research-claim-text">{String(body.text ?? "")}</p>;
		case "source": {
			const s = body as unknown as SourceBody;
			return (
				<div className="research-block">
					<p className="research-meta">
						{[
							`来源状态 ${sourceStatusLabel(s.sourceStatus)}`,
							s.fileRef?.path,
							s.url,
							s.citekey,
							s.fileRef?.sha256 ? `SHA-256 ${s.fileRef.sha256.slice(0, 12)}` : "",
							s.fileRef?.capturedAt ? `快照 ${timeLabel(s.fileRef.capturedAt)}` : "",
							s.fileRef?.lastCheckedAt ? `核对 ${timeLabel(s.fileRef.lastCheckedAt)}` : "",
						]
							.filter(Boolean)
							.join(" · ")}
					</p>
					{(s.limitations ?? []).map((l, i) => (
						<p key={i} className="research-meta research-limit">
							局限：{l}
						</p>
					))}
				</div>
			);
		}
		case "draft": {
			const d = body as unknown as DraftBody;
			const original = "path" in d.originalRef ? d.originalRef.path : d.originalRef.url;
			return (
				<div className="research-block">
					<p className="research-meta">
						{formatLabel(d.format)} · {original} · 锚点 {anchorStatusLabel(d.anchorStatus)} · 原始来源 {sourceStatusLabel(d.originalRef.sourceStatus)}
					</p>
					{d.limitations.map((l, i) => (
						<p key={`l${i}`} className="research-meta research-limit">
							局限：{l}
						</p>
					))}
					{d.findings.map((f, i) => (
						<p key={`f${i}`} className="research-notice source_problem">
							导入问题：{f}
						</p>
					))}
					{d.anchors.map((a) => (
						<div key={a.id} className="research-evidence">
							<p className="research-meta">
								锚点 {a.id} · {locatorLabel(a.locator)} · {anchorStatusLabel(a.anchorStatus)}
								{a.claimIds.length > 0 && ` · 主张 ${a.claimIds.join("、")}`}
							</p>
							<blockquote className="research-quote">{a.quote}</blockquote>
						</div>
					))}
				</div>
			);
		}
		case "evidence": {
			const e = body as unknown as EvidenceBody;
			return (
				<div className="research-block">
					<blockquote className="research-quote">{e.passage.text}</blockquote>
					<p className="research-meta">
						{readingLabel(e.reading)} · {relationLabel(e.relation)} · 来源 {e.sourceId} r{e.sourceRevision} · {e.passage.path} {locatorLabel(e.passage.locator)} · 主张 {e.claimId} r{e.claimRevision} ·{" "}
						{e.quoteCheck.status === "matched" ? "原文已核对" : "未核对原文"}
					</p>
					{e.conditions && <p className="research-meta">条件：{e.conditions}</p>}
					{e.limitations && <p className="research-meta research-limit">局限：{e.limitations}</p>}
				</div>
			);
		}
		case "assessment": {
			const a = body as unknown as AssessmentBody;
			return (
				<div className="research-block">
					<p className="research-meta">
						存在性 {existenceLabel(a.existence)} · 支持度 {supportLabel(a.support)} · 代理评估 · {a.assessor} · 主张 {a.claimId} r{a.claimRevision}
					</p>
					{a.rationale && <p>{a.rationale}</p>}
					{a.checks.length > 0 && (
						<ul className="research-list">
							{a.checks.map((c, i) => (
								<li key={i}>{c}</li>
							))}
						</ul>
					)}
					<p className="research-meta">证据：{a.evidence.map((e) => `${e.id} r${e.revision}`).join("、") || "无"}</p>
				</div>
			);
		}
		case "acknowledgment": {
			const reviewed = body.reviewed as { draft?: { id: string; revision: number } } | undefined;
			return (
				<div className="research-block">
					<p className="research-meta">
						草稿 {String(body.draftId)}
						{reviewed?.draft ? ` r${reviewed.draft.revision}` : ""} · 锚点 {String(body.anchorId)} · 已复查
					</p>
					{typeof body.note === "string" && body.note && <p>{body.note}</p>}
				</div>
			);
		}
		default:
			return null;
	}
}

/** Paragraphs whose evidence changed since they were anchored; acknowledging one binds the revisions shown. */
function ImpactList({
	scope,
	impacts,
	drafts,
	onChanged,
	onError,
}: {
	scope: ResearchScopeRef;
	impacts: EvidenceImpacts;
	drafts: EvidenceSummary["drafts"];
	onChanged: () => void;
	onError: (message: string | null) => void;
}) {
	const [busy, setBusy] = useState<string | null>(null);

	async function acknowledge(item: ImpactItem) {
		const at = `${item.draftId}:${item.anchorId}:${item.evidenceId ?? ""}`;
		setBusy(at);
		onError(null);
		try {
			await api.researchAcknowledge(scope, acknowledgeRequest(item));
			onChanged();
		} catch (err) {
			onError(err instanceof ApiError && err.status === 409 ? "复查失败：段落已有新版本，已重新加载" : `复查失败：${failure(err)}`);
			onChanged();
		} finally {
			setBusy(null);
		}
	}

	if (impacts.paragraphs.length === 0) return <p className="hint">暂无受影响段落</p>;
	return (
		<article className="note-view research-view">
			<p className="note-meta">更新于 {timeLabel(impacts.snapshotAt)}</p>
			{impacts.paragraphs.map((item) => {
				const at = `${item.draftId}:${item.anchorId}:${item.evidenceId ?? ""}`;
				const draft = drafts.find((d) => d.id === item.draftId);
				return (
					<div key={at} className="research-block research-claim">
						<p className="research-meta">
							<span className={`research-state ${item.status}`}>{impactStatusLabel(item.status)}</span> {draft?.title ?? draft?.path ?? item.draftId} r{item.draftRevision} · 锚点 {item.anchorId} ·{" "}
							{anchorStatusLabel(item.anchorStatus)}
							{item.movedTo && ` · 现位于${locatorLabel(item.movedTo)}`} · {item.acknowledged ? "已复查" : "待复查"}
						</p>
						<blockquote className="research-quote">{item.quote}</blockquote>
						<p className="hint">{item.reasons.length ? item.reasons.join("；") : item.reason}</p>
						<Links targets={researchArtifactTargets(scope, [item])} />
						{!item.acknowledged && (
							<div className="row">
								<button disabled={busy !== null} onClick={() => void acknowledge(item)}>
									{busy === at ? BUSY_LABELS.acknowledge : "确认复查"}
								</button>
							</div>
						)}
					</div>
				);
			})}
		</article>
	);
}

/**
 * What the scope's stored cases and comparisons support saying about quality, never a role's self-score: the summary,
 * each regression case with its state, and every comparison's results per case with the versions that differed.
 */
function QualityDetail({ summary, reads, onPick }: { summary: QualitySummary; reads: RecordRead[]; onPick: (id: string) => void }) {
	const supported = (kind: string) => reads.filter((r): r is RecordRead & { record: ResearchRecord } => !r.record.unsupported && r.record.kind === kind);
	const cases = supported("regression_case");
	const comparisons = supported("quality_comparison")
		.map((r) => r.record)
		.filter((r) => r.status !== "rejected")
		.reverse();
	return (
		<article className="note-view research-view">
			<p className="note-meta">更新于 {timeLabel(summary.snapshotAt)}</p>
			<QualitySummaryCard summary={summary} />
			<div className="research-block">
				<p className="research-subtitle">回归用例</p>
				{cases.length === 0 ? (
					<p className="hint">暂无回归用例</p>
				) : (
					<ul className="research-list">
						{cases.map(({ record: r, latest: pending }) => (
							<li key={r.id}>
								<button className="link-btn" onClick={() => onPick(r.id)}>
									{researchRecordTitle(r)}
								</button>
								<span className="hint">
									{" "}
									r{r.revision} · {revisionState(r)}
									{pending ? ` · r${pending.revision} ${revisionState(pending)}` : ""}
								</span>
							</li>
						))}
					</ul>
				)}
			</div>
			<div className="research-block">
				<p className="research-subtitle">复发</p>
				<RecurrenceTable comparisons={comparisons} cases={cases.length} />
			</div>
		</article>
	);
}
